import { useState, useEffect, useCallback } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { StripePrice, Sponsor } from '@/utils/types';
import countries from '@/data/countries.json';
import { useTranslation } from 'react-i18next';
import { useCartContext } from '@/contexts/cartContext';
import { useAuthContext } from '@/contexts/authContext';
import { cn, smoothScrollToError } from '@/utils/commons';
import { getSponsors, createSubscriptionIntent } from '@/utils/api';
import { IconInvoice, IconTrees, IconCreditCardPay } from '@tabler/icons-react';
import PaymentDialog from '@/components/pages/subscription/paymentDialog/index';
import { frenchTerritories } from '@/utils/config';
import { FormError } from '@/components/commons/formError';
import SimpleLoader from '@/components/loaders/simpleLoader';

export default function PaymentForm({ prices }: { prices: StripePrice[] }) {
  const { t } = useTranslation();
  const { account } = useAuthContext();

  const { countryCode, isVatNumberValid, currency, vatRate, coupon, setCartPartialState } = useCartContext();
  const [subscriptionIntentLoading, setSubscriptionIntentLoading] = useState(false);
  const [canFocus, setCanFocus] = useState(true);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const fieldRequiredText = t('Web_FormValidation_Required');

  const schema = yup
    .object({
      firstname: yup.string().required(fieldRequiredText),
      name: yup.string().required(fieldRequiredText),
      email: yup.string().email(t('ErrorMessage_EnterValidEmail')).required(fieldRequiredText),
      address: yup.string().required(fieldRequiredText),
      postcode: yup.string().required(fieldRequiredText),
      city: yup.string().required(fieldRequiredText),
      state: yup.string(),
      country: yup.string().required(fieldRequiredText),
      company: yup.string(),
      sponsor: yup.string(),
      vatCode: yup.string(),
    })
    .required();

  type FormData = yup.InferType<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    shouldFocusError: false,
    resolver: yupResolver(schema),
    defaultValues: {
      firstname: account?.properties.firstname || '',
      name: account?.properties.lastname || '',
      email: account?.properties.email || '',
      address: account?.properties.address || '',
      postcode: account?.properties.zipcode || '',
      city: account?.properties.town || '',
      state: account?.properties.state || '',
      company: account?.properties.company || '',
    },
  });

  const watchVatCode = watch('vatCode');
  const watchCountry = watch('country');
  const watchSponsor = watch('sponsor');
  const watchPostcode = watch('postcode');

  const onError = () => {
    setCanFocus(true);
  };

  const updateSponsor = useCallback(
    (sponsorId: string) => {
      setValue('sponsor', sponsorId);
      const sponsor = sponsors.find(sponsor => sponsor?.id === sponsorId);
      setCartPartialState({ sponsor });
    },
    [setCartPartialState, setValue, sponsors]
  );

  useEffect(() => {
    setCartPartialState({
      prices,
    });

    if (countryCode) {
      setValue('country', countryCode);
    }

    getSponsors().then(sponsorList => {
      setSponsors(sponsorList);
    });
  }, [countryCode, prices, setCartPartialState, setValue]);

  useEffect(() => {
    if (sponsors.length > 0) {
      updateSponsor(sponsors[0].id);
    }
  }, [sponsors, updateSponsor]);

  useEffect(() => {
    if (countryCode) {
      setValue('country', countryCode);
    }
  }, [countryCode, setValue]);

  useEffect(() => {
    if (errors && canFocus) {
      smoothScrollToError(errors, setCanFocus);
    }
  }, [errors, canFocus]); // watch errors and scroll to first error

  useEffect(() => {
    if (watchPostcode) {
      setCartPartialState({ postCode: watchPostcode });
    }
  }, [watchPostcode, setCartPartialState]);

  useEffect(() => {
    if (watchVatCode) {
      setCartPartialState({ vatCode: watchVatCode });
    }
  }, [watchVatCode, setCartPartialState]);

  const handleContinueWithPayment = async (formData: FormData) => {
    setSubscriptionIntentLoading(true);
    setErrorMessage(undefined);

    try {
      const {
        status,
        error: customerError,
        data: { clientSecret, customerId, subscriptionId },
      } = await createSubscriptionIntent({
        formData,
        items: prices.map(price => ({ price: price.id, quantity: 1 })),
        currency,
        taxRate: vatRate || 0,
        couponId: coupon?.id,
      });

      if (!customerError && status === 200) {
        setClientSecret(clientSecret);
        setCartPartialState({ stripeCustomerId: customerId, stripeSubscriptionId: subscriptionId });
        setFormData(formData); // Store form data for payment dialog
        setIsPaymentModalOpen(true);
      } else if (customerError) {
        console.error('Customer creation error', customerError);
        setErrorMessage(customerError);
      } else {
        console.error('Customer creation failed');
        setErrorMessage(t('ErrorMessage_ErrorMessage'));
      }
    } catch (error) {
      console.error('Customer creation error', error);
      setErrorMessage((error as Error)?.message || t('ErrorMessage_ErrorMessage'));
    } finally {
      setSubscriptionIntentLoading(false);
    }
  };

  return (
    <>
      <Card className="flex-1 lg:pr-8 lg:border-r border-border">
        <CardHeader>
          <h3 className="text-xl font-bold mb-6 flex items-center">
            <IconInvoice className="mr-2" />
            {t('payment_billing_infos')}
          </h3>
        </CardHeader>
        <CardContent>
          <form className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstname" className="required">
                  {t('Global_FirstName')}
                </Label>
                <Input id="firstname" {...register('firstname')} error={errors.firstname?.message} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name" className="required">
                  {t('Global_Name')}
                </Label>
                <Input id="name" {...register('name')} error={errors.name?.message} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="capitalize required" htmlFor="email">
                {t('Global_Email')}
              </Label>
              <Input id="email" type="email" {...register('email')} error={errors.email?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address" className="required">
                {t('Global_Address')}
              </Label>
              <Input id="address" {...register('address')} error={errors.address?.message} />
            </div>
            <div className={cn('grid gap-4', watchCountry === 'US' ? 'grid-cols-3' : 'grid-cols-2')}>
              <div className="space-y-2">
                <Label htmlFor="postcode" className="required">
                  {t('Global_ZipCode')}
                </Label>
                <Input id="postcode" {...register('postcode')} error={errors.postcode?.message} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city" className="required">
                  {t('Global_Town')}
                </Label>
                <Input id="city" {...register('city')} error={errors.city?.message} />
              </div>
              {watchCountry === 'US' && (
                <div className="space-y-2">
                  <Label htmlFor="state">{t('Payment_State')}</Label>
                  <Input id="state" {...register('state')} />
                  {errors.state && <FormError error={errors.state.message} />}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="country" className="required">
                {t('Global_Country')}
              </Label>
              <Select
                value={watchCountry}
                onValueChange={value => {
                  setValue('country', value);
                  setCartPartialState({ countryCode: value });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countries
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        <span className="flex items-center">
                          <img
                            src={`/images/flags/${country.code.toLowerCase()}.svg`}
                            alt={country.name}
                            className="w-4 h-4 inline mr-2"
                          />
                          {`${country.name}`}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {errors.country && <FormError error={errors.country.message} />}
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">{t('PDF_Company')}</Label>
              <Input id="company" {...register('company')} />
              {errors.company && <FormError error={errors.company.message} />}
            </div>

            {sponsors.length > 0 && frenchTerritories.includes(countryCode) && (
              // Only show sponsor field if the user is in France or french territories
              <div className="space-y-2">
                <Label htmlFor="country">
                  {`${t('Web_Sponsorship_Your_Sponsor')} `}
                  <IconTrees className="inline-block h-4 w-4 text-green-600" />
                </Label>
                <Select value={watchSponsor} onValueChange={value => updateSponsor(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sponsors
                      .filter(Boolean)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(sponsor => (
                        <SelectItem key={sponsor.id} value={sponsor.id}>
                          {sponsor.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="vatCode">{t('payment_vat_number')}</Label>
              <Input
                id="vatCode"
                {...register('vatCode')}
                status={
                  watchVatCode
                    ? isVatNumberValid === 'valid'
                      ? 'success'
                      : isVatNumberValid === 'invalid'
                      ? 'error'
                      : undefined
                    : undefined
                }
              />
              {errors.vatCode && <FormError error={errors.vatCode.message} />}

              {watchVatCode && (
                <p
                  className={cn(
                    `text-sm ${
                      isVatNumberValid === 'valid'
                        ? 'text-green-600'
                        : isVatNumberValid === 'invalid'
                        ? 'text-red-600'
                        : ''
                    }`
                  )}
                >
                  {isVatNumberValid === 'valid'
                    ? t('payment_valid_vat_code')
                    : isVatNumberValid === 'invalid'
                    ? t('payment_invalid_vat_code')
                    : ''}
                </p>
              )}
            </div>

            <Button
              type="button"
              className="w-full"
              disabled={subscriptionIntentLoading}
              onClick={handleSubmit(handleContinueWithPayment, onError)}
            >
              <span className="flex items-center justify-center">
                {subscriptionIntentLoading ? t('Global_Loading') : t('payment_continue')}
                {!subscriptionIntentLoading && <IconCreditCardPay className="ml-2 w-5 h-5" />}
              </span>
            </Button>
            {errorMessage && <FormError error={errorMessage} />}
          </form>
        </CardContent>
      </Card>

      {clientSecret && (
        <PaymentDialog
          clientSecret={clientSecret}
          isOpen={isPaymentModalOpen && formData !== null}
          onOpenChange={setIsPaymentModalOpen}
        />
      )}

      <SimpleLoader isLoading={subscriptionIntentLoading} mode="modal" />
    </>
  );
}
